import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { date, money, moneyByCurrency, parseAmount, toInput, todayIso } from '../lib/format';
import { useBankAccounts, useCustomers, useProducts, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Invoice, InvoiceLine } from '../lib/types';
import { Icons } from '../components/icons';
import { NeedBankAccount } from './Cash';
import { NEW_PRODUCT, NewProductModal } from './Products';
import { Badge, Button, ButtonLink, Card, Empty, ErrorNote, Field, Input, KeyValues, Modal, MoneyInput, PageHeader, Query, Select, SourceBadge, StatusBadge, Table, Tabs, Td, Th, Toggle, Tr } from '../components/ui';

export const isOverdue = (i: Pick<Invoice, 'status' | 'dueDate'>) => (i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID') && !!i.dueDate && i.dueDate < todayIso();

export function InvoiceStatus({ i }: { i: Pick<Invoice, 'status' | 'dueDate'> }) {
  if (isOverdue(i)) return <StatusBadge status="OVERDUE" />;
  if (i.status === 'DRAFT') return <StatusBadge status="DRAFT" label="Scheduled" />;
  return <StatusBadge status={i.status} />;
}

type Tab = 'open' | 'paid' | 'scheduled' | 'void' | 'all';

export function InvoicesPage() {
  const navigate = useNavigate();
  const customers = useCustomers();
  const { currency, can } = useSession();
  const [tab, setTab] = useState<Tab>('open');
  const filter = tab === 'open' ? { open: true } : tab === 'paid' ? { status: 'PAID' } : tab === 'scheduled' ? { status: 'DRAFT' } : tab === 'void' ? { status: 'VOID' } : {};
  const q = useApi<Invoice[]>(`/api/invoices${qs({ ...filter, limit: 300 })}`);
  const rows = (q.data ?? []).slice().sort((a, b) => (tab === 'scheduled' ? a.issueDate.localeCompare(b.issueDate) : b.issueDate.localeCompare(a.issueDate)));
  const openTotal = tab === 'open' ? moneyByCurrency(rows.map((i) => ({ amount: i.total - i.amountPaid, currency: i.currency })), currency) : null;

  return (
    <>
      <PageHeader
        title="Invoices"
        description="Contract billing schedules create scheduled invoices; the continuous-close scheduler issues each one on its date and posts it to AR and deferred revenue."
        actions={
          can('ACCOUNTANT') && (
            <ButtonLink to="/ar/invoices/new" variant="primary" icon={<Icons.Plus size={15} />}>
              New invoice
            </ButtonLink>
          )
        }
      />
      <Tabs
        className="mb-4"
        value={tab}
        onChange={setTab}
        items={[
          { key: 'open', label: 'Open' },
          { key: 'scheduled', label: 'Scheduled' },
          { key: 'paid', label: 'Paid' },
          { key: 'void', label: 'Void' },
          { key: 'all', label: 'All' },
        ]}
      />
      <Card pad={false} title={openTotal !== null ? `Open receivables: ${openTotal}` : undefined}>
        <Query q={q}>
          {() =>
            rows.length === 0 ? (
              <Empty title={tab === 'open' ? 'Nothing open to collect' : 'No invoices here'}>
                {tab === 'open' && 'Issue an invoice by hand, or add a contract and its billing schedule creates them for you.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Number</Th>
                    <Th>Customer</Th>
                    <Th>Issued</Th>
                    <Th>Due</Th>
                    <Th right>Total</Th>
                    <Th right>Balance</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((i) => (
                    <Tr key={i.id} onClick={() => navigate(`/ar/invoices/${i.id}`)}>
                      <Td className="font-medium">{i.number}</Td>
                      <Td>{customers.byId.get(i.customerId)?.name}</Td>
                      <Td className="num whitespace-nowrap">{date(i.issueDate)}</Td>
                      <Td className="num whitespace-nowrap">{date(i.dueDate)}</Td>
                      <Td right>{money(i.total, i.currency)}</Td>
                      <Td right className={isOverdue(i) ? 'text-bad' : ''}>
                        {i.status === 'VOID' || i.status === 'DRAFT' ? '—' : money(i.total - i.amountPaid, i.currency)}
                      </Td>
                      <Td>
                        <InvoiceStatus i={i} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
    </>
  );
}

export function InvoicePage() {
  const { id } = useParams();
  const { can } = useSession();
  const customers = useCustomers();
  const products = useProducts();
  const q = useApi<{ invoice: Invoice; lines: InvoiceLine[] }>(`/api/invoices/${id}`);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const issue = useAction(() => api(`/api/invoices/${id}/issue`, { method: 'POST' }), { success: 'Invoice issued and posted to AR' });
  const voidIt = useAction(() => api(`/api/invoices/${id}/void`, { body: {} }), { success: 'Invoice voided; postings reversed' });

  return (
    <Query q={q}>
      {({ invoice: i, lines }) => {
        const c = customers.byId.get(i.customerId);
        const balance = i.total - i.amountPaid;
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/ar/invoices" className="hover:text-ink">
                  ← Invoices
                </Link>
              }
              title={
                <span className="flex items-center gap-3">
                  {i.number} <InvoiceStatus i={i} />
                </span>
              }
              description={i.memo}
              actions={
                can('ACCOUNTANT') && (
                  <>
                    {i.status === 'DRAFT' && (
                      <Button variant="primary" loading={issue.isPending} onClick={() => issue.mutate()}>
                        Issue now
                      </Button>
                    )}
                    {(i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID') && (
                      <Button variant="primary" icon={<Icons.Bank size={15} />} onClick={() => setPaying(true)}>
                        Record payment
                      </Button>
                    )}
                    {i.status === 'ISSUED' && i.amountPaid === 0 && (
                      <Button variant="danger" onClick={() => setVoiding(true)}>
                        Void
                      </Button>
                    )}
                  </>
                )
              }
            />
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
              <Card pad={false} title="Lines">
                <Table>
                  <thead>
                    <tr>
                      <Th>Item</Th>
                      <Th>Service period</Th>
                      <Th>Recognition</Th>
                      <Th right>Qty</Th>
                      <Th right>Unit price</Th>
                      <Th right>Amount</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.id}>
                        <Td>
                          <div className="font-medium">{(l.productId && products.byId.get(l.productId)?.name) || l.description}</div>
                          {l.productId && <div className="text-[12px] text-muted">{l.description}</div>}
                        </Td>
                        <Td muted className="whitespace-nowrap">
                          {l.serviceStart ? `${date(l.serviceStart, { year: false })} – ${date(l.serviceEnd)}` : '—'}
                        </Td>
                        <Td>{l.deferred ? <Badge tone="info">Deferred, over time</Badge> : <Badge>At issue</Badge>}</Td>
                        <Td right>{Number(l.quantity).toLocaleString()}</Td>
                        <Td right>{money(l.unitPrice, i.currency)}</Td>
                        <Td right>{money(l.amount, i.currency)}</Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="text-[13px]">
                    {[
                      ['Subtotal', i.subtotal],
                      ['Tax', i.tax],
                      ['Total', i.total],
                      ['Paid', i.amountPaid ? -i.amountPaid : 0],
                    ].map(([k, v]) => (
                      <tr key={k as string}>
                        <Td colSpan={5} className="border-b-0 py-1! text-right text-muted">
                          {k}
                        </Td>
                        <Td right className="border-b-0 py-1!">
                          {money(v as number, i.currency)}
                        </Td>
                      </tr>
                    ))}
                    <tr>
                      <Td colSpan={5} className="border-b-0 text-right font-semibold">
                        Balance due
                      </Td>
                      <Td right className="border-b-0 text-[15px] font-semibold">
                        {money(i.status === 'VOID' ? 0 : balance, i.currency)}
                      </Td>
                    </tr>
                  </tfoot>
                </Table>
              </Card>
              <Card title="Details">
                <KeyValues
                  cols={1}
                  items={[
                    ['Customer', c ? <Link to={`/ar/customers/${c.id}`} className="text-brand hover:underline">{c.name}</Link> : '—'],
                    ['Issue date', date(i.issueDate)],
                    ['Due date', date(i.dueDate)],
                    ['Service period', i.servicePeriodStart ? `${date(i.servicePeriodStart)} – ${date(i.servicePeriodEnd)}` : '—'],
                    ['Contract', i.contractId ? <Link to={`/ar/contracts/${i.contractId}`} className="text-brand hover:underline">View contract →</Link> : '—'],
                    ['Journal entry', i.journalEntryId ? <Link to={`/journal/${i.journalEntryId}`} className="text-brand hover:underline">View posting →</Link> : 'Posts when issued'],
                    ['Channel', <SourceBadge key="s" source={i.submissionSource} />],
                  ]}
                />
              </Card>
            </div>
            {c && <RecordPaymentModal open={paying} onClose={() => setPaying(false)} customerId={c.id} currency={i.currency} defaultAmount={balance} invoiceId={i.id} />}
            <Modal
              open={voiding}
              onClose={() => setVoiding(false)}
              title={`Void ${i.number}`}
              footer={
                <>
                  <Button onClick={() => setVoiding(false)}>Cancel</Button>
                  <Button variant="danger" loading={voidIt.isPending} onClick={() => voidIt.mutate(undefined, { onSuccess: () => setVoiding(false) })}>
                    Void invoice
                  </Button>
                </>
              }
            >
              <p className="text-[13px] text-muted">Voiding reverses the AR posting and undoes any revenue already recognized from this invoice with a targeted entry. This cannot be undone.</p>
            </Modal>
          </>
        );
      }}
    </Query>
  );
}

export function RecordPaymentModal({ open, onClose, customerId, currency, defaultAmount, invoiceId }: { open: boolean; onClose: () => void; customerId: string; currency: string; defaultAmount?: number; invoiceId?: string }) {
  const banks = useBankAccounts();
  const options = banks.active.filter((b) => b.currency === currency);
  const [bankId, setBankId] = useState('');
  const [d, setD] = useState(todayIso());
  const [amount, setAmount] = useState(toInput(defaultAmount, currency));
  const [reference, setReference] = useState('');
  const minor = parseAmount(amount, currency);
  const record = useAction(
    () =>
      api('/api/payments', {
        body: { customerId, bankAccountId: bankId || options[0]?.id, date: d, amount: minor, reference: reference || undefined, applications: invoiceId ? [{ invoiceId, amount: Math.min(minor ?? 0, defaultAmount ?? Infinity) }] : undefined },
      }),
    { success: 'Payment recorded: Dr Cash, Cr AR' },
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a customer payment"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!minor || minor <= 0 || !options.length} loading={record.isPending} onClick={() => record.mutate(undefined, { onSuccess: onClose })}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Deposited to" className="col-span-2">
          {options.length || banks.isLoading ? (
            <Select value={bankId || options[0]?.id || ''} onChange={(e) => setBankId(e.target.value)}>
              {options.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.mask ? ` ··${b.mask}` : ''}
                </option>
              ))}
            </Select>
          ) : (
            <NeedBankAccount currency={currency} />
          )}
        </Field>
        <Field label="Date">
          <Input type="date" value={d} onChange={(e) => setD(e.target.value)} />
        </Field>
        <Field label="Amount">
          <MoneyInput currency={currency} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Reference" className="col-span-2" hint={invoiceId ? 'Applied to this invoice; any excess stays unapplied on the customer' : 'Applied to the oldest open invoices first'}>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Wire or check reference" />
        </Field>
      </div>
      {record.error && <ErrorNote error={record.error} className="mt-3" />}
    </Modal>
  );
}

interface DraftInvLine {
  key: number;
  productId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  serviceStart: string;
  serviceEnd: string;
}
let k = 0;
const blank = (): DraftInvLine => ({ key: ++k, productId: '', description: '', quantity: '1', unitPrice: '', serviceStart: '', serviceEnd: '' });

export function NewInvoicePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const customers = useCustomers();
  const products = useProducts();
  const subs = useSubsidiaries();
  const [customerId, setCustomerId] = useState(params.get('customerId') ?? '');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [memo, setMemo] = useState('');
  const [taxPct, setTaxPct] = useState('');
  const [issueNow, setIssueNow] = useState(true);
  const [lines, setLines] = useState<DraftInvLine[]>([blank()]);
  const [newFor, setNewFor] = useState<number | null>(null);
  const customer = customers.byId.get(customerId);
  const currency = customer?.currency ?? 'USD';
  const parsed = lines.map((l) => ({ ...l, unit: parseAmount(l.unitPrice, currency), qty: Number(l.quantity) || 0 }));
  const subtotal = parsed.reduce((s, l) => s + Math.round((l.unit ?? 0) * l.qty), 0);
  const tax = taxPct ? Math.round((subtotal * Number(taxPct)) / 100) : 0;
  const valid = !!customer && parsed.every((l) => l.unit !== null && l.qty > 0 && (l.productId || l.description));
  const create = useAction(
    () =>
      api<{ invoice: Invoice }>('/api/invoices', {
        body: {
          customerId,
          issueDate,
          dueDate: dueDate || undefined,
          memo: memo || undefined,
          taxRate: taxPct ? (Number(taxPct) / 100).toFixed(6).replace(/0+$/, '').replace(/\.$/, '') : undefined,
          issue: issueNow,
          lines: parsed.map((l) => ({ productId: l.productId || undefined, description: l.description || undefined, quantity: l.quantity, unitPrice: l.unit, serviceStart: l.serviceStart || undefined, serviceEnd: l.serviceEnd || undefined })),
        },
      }),
    { success: issueNow ? 'Invoice issued' : 'Draft invoice saved' },
  );
  const up = (key: number, patch: Partial<DraftInvLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const due = dueDate || (customer ? addDays(issueDate, customer.paymentTermsDays) : '');

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/ar/invoices" className="hover:text-ink">
            ← Invoices
          </Link>
        }
        title="New invoice"
        description="Lines with a service period that spans time are deferred and recognized daily; others are recognized when issued."
      />
      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Customer" className="md:col-span-2" hint={customer ? `${customer.currency} · ${subs.byId.get(customer.subsidiaryId)?.name} · net ${customer.paymentTermsDays}` : undefined}>
            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">Select customer…</option>
              {customers.list.filter((c) => !c.archivedAt || c.id === customerId).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Issue date">
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label="Due date">
            <Input type="date" value={due} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Field label="Memo" className="md:col-span-3">
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </Field>
          <Field label="Tax rate (%)">
            <Input inputMode="decimal" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} placeholder="0" />
          </Field>
        </div>
      </Card>
      <Card className="mt-4" pad={false}>
        <Table>
          <thead>
            <tr>
              <Th w="22%">Product</Th>
              <Th>Description</Th>
              <Th w="80px">Qty</Th>
              <Th w="140px">Unit price</Th>
              <Th w="150px">Service start</Th>
              <Th w="150px">Service end</Th>
              <Th right w="120px">
                Amount
              </Th>
              <Th w="36px" />
            </tr>
          </thead>
          <tbody>
            {parsed.map((l) => (
              <tr key={l.key}>
                <Td>
                  <Select value={l.productId} onChange={(e) => (e.target.value === NEW_PRODUCT ? setNewFor(l.key) : up(l.key, { productId: e.target.value }))}>
                    <option value="">No product</option>
                    {products.list.filter((p) => !p.archivedAt || p.id === l.productId).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                    <option value={NEW_PRODUCT}>+ New product…</option>
                  </Select>
                </Td>
                <Td>
                  <Input value={l.description} onChange={(e) => up(l.key, { description: e.target.value })} />
                </Td>
                <Td>
                  <Input inputMode="decimal" value={l.quantity} onChange={(e) => up(l.key, { quantity: e.target.value })} />
                </Td>
                <Td>
                  <MoneyInput value={l.unitPrice} onChange={(e) => up(l.key, { unitPrice: e.target.value })} placeholder="0.00" />
                </Td>
                <Td>
                  <Input type="date" value={l.serviceStart} onChange={(e) => up(l.key, { serviceStart: e.target.value })} />
                </Td>
                <Td>
                  <Input type="date" value={l.serviceEnd} onChange={(e) => up(l.key, { serviceEnd: e.target.value })} />
                </Td>
                <Td right>{money(Math.round((l.unit ?? 0) * l.qty), currency)}</Td>
                <Td>
                  <button onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))} className="rounded p-1 text-faint hover:text-bad" aria-label="Remove line">
                    <Icons.Trash size={15} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td colSpan={6} className="border-b-0">
                <Button size="sm" variant="ghost" icon={<Icons.Plus size={14} />} onClick={() => setLines((ls) => [...ls, blank()])}>
                  Add line
                </Button>
              </Td>
              <Td right className="border-b-0 text-[12.5px] text-muted">
                {tax ? `+ ${money(tax, currency)} tax` : ''}
                <div className="text-[14px] font-semibold text-ink">{money(subtotal + tax, currency)}</div>
              </Td>
              <Td className="border-b-0" />
            </tr>
          </tfoot>
        </Table>
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
          <div className="w-80">
            <Toggle checked={issueNow} onChange={setIssueNow} label="Issue immediately" description="Posts Dr AR / Cr revenue or deferred revenue now" />
          </div>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => navigate(`/ar/invoices/${r.invoice.id}`) })}>
            {issueNow ? 'Create and issue' : 'Save draft'}
          </Button>
        </div>
        {create.error && <ErrorNote error={create.error} className="mx-4 mb-4" />}
      </Card>
      <NewProductModal open={newFor !== null} onClose={() => setNewFor(null)} onCreated={(p) => newFor !== null && up(newFor, { productId: p.id })} />
    </>
  );
}

function addDays(d: string, n: number) {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}
